"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get BinaryScoreOutcomeSchema () {
        return BinaryScoreOutcomeSchema;
    },
    get EmployeeSummaryEvalRunDocument () {
        return EmployeeSummaryEvalRunDocument;
    },
    get EvalCaseResultSchema () {
        return EvalCaseResultSchema;
    },
    get EvalRunSummarySchema () {
        return EvalRunSummarySchema;
    },
    get EvalSamplingStrategySchema () {
        return EvalSamplingStrategySchema;
    },
    get GroundTruthStrategyResultSchema () {
        return GroundTruthStrategyResultSchema;
    },
    get PairwiseVerdictSchema () {
        return PairwiseVerdictSchema;
    },
    get PersonaArchetypeSchema () {
        return PersonaArchetypeSchema;
    },
    get PersonaAxesSchema () {
        return PersonaAxesSchema;
    },
    get PersonaRoleContextSchema () {
        return PersonaRoleContextSchema;
    },
    get PersonaSparsitySchema () {
        return PersonaSparsitySchema;
    },
    get PersonaTrajectorySchema () {
        return PersonaTrajectorySchema;
    },
    get RubricDimensionSchema () {
        return RubricDimensionSchema;
    },
    get StratumIdSchema () {
        return StratumIdSchema;
    },
    get WinRateSchema () {
        return WinRateSchema;
    }
});
const _bson = require("bson");
const _zod = require("zod");
const EvalSamplingStrategySchema = _zod.z.enum([
    "first_n_recent",
    "embedding_top_k",
    "stratified_time"
]);
const StratumIdSchema = _zod.z.enum([
    "thin",
    "typical",
    "heavy",
    "long_tenure"
]);
const RubricDimensionSchema = _zod.z.enum([
    "grounding",
    "specificity",
    "person_centeredness",
    "actionability",
    "safety",
    "coverage",
    "coherence"
]);
const BinaryScoreOutcomeSchema = _zod.z.object({
    passed: _zod.z.boolean(),
    details: _zod.z.string().optional()
});
const PairwiseVerdictSchema = _zod.z.object({
    preferred: _zod.z.union([
        EvalSamplingStrategySchema,
        _zod.z.literal("equal")
    ]),
    confidence: _zod.z.number(),
    reasoning: _zod.z.string()
});
const PersonaArchetypeSchema = _zod.z.enum([
    "high_performer",
    "steady",
    "struggling",
    "new_hire",
    "coasting_veteran"
]);
const PersonaTrajectorySchema = _zod.z.enum([
    "improving",
    "stable",
    "declining",
    "volatile"
]);
const PersonaSparsitySchema = _zod.z.enum([
    "full_data",
    "no_peer_feedback",
    "no_manager_1on1",
    "only_self_review",
    "no_goals"
]);
const PersonaRoleContextSchema = _zod.z.enum([
    "ic_no_reports",
    "ic_remote",
    "tl_with_reports",
    "manager_small",
    "manager_large"
]);
const PersonaAxesSchema = _zod.z.object({
    archetype: PersonaArchetypeSchema,
    trajectory: PersonaTrajectorySchema,
    sparsity: PersonaSparsitySchema,
    role_context: PersonaRoleContextSchema
});
const GroundTruthStrategyResultSchema = _zod.z.object({
    archetype_identified: _zod.z.object({
        passed: _zod.z.boolean(),
        predicted: _zod.z.string()
    }),
    trajectory_surfaced: _zod.z.object({
        passed: _zod.z.boolean(),
        predicted: _zod.z.string().nullable(),
        reason: _zod.z.string()
    }),
    persona_fidelity: _zod.z.object({
        score: _zod.z.number(),
        reasoning: _zod.z.string().optional()
    })
});
const EvalCaseResultSchema = _zod.z.object({
    case_id: _zod.z.string(),
    employee_id: _zod.z.instanceof(_bson.ObjectId),
    // Core data-volume strata for real runs; archetype/trajectory bucket names for synthetic runs.
    stratum: _zod.z.string(),
    outputs: _zod.z.record(EvalSamplingStrategySchema, _zod.z.unknown()),
    binary_scores: _zod.z.record(_zod.z.string(), _zod.z.record(EvalSamplingStrategySchema, BinaryScoreOutcomeSchema)),
    // Null when the rubric judge failed for that strategy.
    ordinal_scores: _zod.z.record(EvalSamplingStrategySchema, _zod.z.record(RubricDimensionSchema, _zod.z.number()).nullable()),
    pairwise: PairwiseVerdictSchema.nullable(),
    sampling_metadata: _zod.z.record(EvalSamplingStrategySchema, _zod.z.object({
        consideredReviewIds: _zod.z.array(_zod.z.string()),
        consideredFeedbackIds: _zod.z.array(_zod.z.string()),
        rejectionReason: _zod.z.string().nullable().optional(),
        samplingDurationMs: _zod.z.number()
    })).optional(),
    persona_axes: PersonaAxesSchema.nullable().optional(),
    ground_truth_scores: _zod.z.record(_zod.z.string(), GroundTruthStrategyResultSchema).nullable().optional()
});
const WinRateSchema = _zod.z.object({
    wins: _zod.z.number(),
    losses: _zod.z.number(),
    ties: _zod.z.number(),
    ci_low: _zod.z.number(),
    ci_high: _zod.z.number()
});
const EvalRunSummarySchema = _zod.z.object({
    binary_pass_rate: _zod.z.record(EvalSamplingStrategySchema, _zod.z.record(_zod.z.string(), _zod.z.number())),
    // Null when the rubric judge failed for every case of that strategy.
    ordinal_means: _zod.z.record(EvalSamplingStrategySchema, _zod.z.record(RubricDimensionSchema, _zod.z.number()).nullable()),
    pairwise_win_rate: _zod.z.record(EvalSamplingStrategySchema, WinRateSchema),
    per_stratum: _zod.z.record(_zod.z.string(), _zod.z.unknown()),
    // Rubric-judge attrition per strategy: cases whose scores were nulled by a
    // failed judge call. Absent on runs recorded before this field existed.
    judge_failures: _zod.z.record(EvalSamplingStrategySchema, _zod.z.number()).optional(),
    scale_note: _zod.z.string().optional()
});
const EmployeeSummaryEvalRunDocument = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    run_id: _zod.z.string(),
    created_at: _zod.z.date(),
    created_by: _zod.z.instanceof(_bson.ObjectId),
    company_id: _zod.z.instanceof(_bson.ObjectId),
    config: _zod.z.object({
        test_set_id: _zod.z.string(),
        as_of: _zod.z.date(),
        strategies_compared: _zod.z.array(EvalSamplingStrategySchema),
        strata: _zod.z.array(StratumIdSchema),
        generator_model: _zod.z.string(),
        judge_model: _zod.z.string(),
        prompt_template_hash: _zod.z.string(),
        schema_version: _zod.z.number(),
        seed: _zod.z.number().optional(),
        synthetic_test_set: _zod.z.boolean().optional().default(false)
    }),
    status: _zod.z.enum([
        "complete",
        "failed"
    ]),
    progress: _zod.z.object({
        completed: _zod.z.number(),
        total: _zod.z.number()
    }),
    cost: _zod.z.object({
        generation_usd: _zod.z.number(),
        judge_usd: _zod.z.number(),
        embedding_usd: _zod.z.number()
    }),
    results: _zod.z.array(EvalCaseResultSchema),
    summary: EvalRunSummarySchema,
    error: _zod.z.string().optional()
});
